/// <amd-module name="Build/Queue-Panel/Stores/QueueBuild" />
import { BuildDefinition, BuildDefinitionVariable, BuildRepository } from "Build/Client/Contracts/Build";
import {
    BuildResourceData,
    BuildResourceRunData,
    BuildResourceRunsRequestParams,
    ContainerResourceData,
    ContainerResourceRunData,
    ContainerResourceRunsRequestParams,
    GeneralSettingsData,
    OrgSettingsData,
    PackageResourceData,
    PipelineResourceData,
    PipelineResourceRunData,
    PipelineResourceRunsRequestParams,
    PipelineRunParametersData,
    PipelineRunParametersRequestParams,
    RepositoryResourceData,
    TemplateParameterData,
    TemplateParameterInputType
} from "Build/Client/Contracts/DataProvider";
import { IBuildRestClient } from "Build/Client/Restclient/Build";
import { ShelvesetPickerActionHub } from "Build/Common/Library/Legacy/Components/ShelvesetAction";
import { QueueActionHub } from "Build/Common/Library/Legacy/Stores/Queue";
import { Store } from "Build/Flux/Store";
import { EnableDiagnosticActionHub, SourceVersionActionHub } from "Build/Queue-Panel/Action";
import { GitConstants } from "Build/Queue-Panel/Constants";
import * as Resources from "Build/Queue-Panel/Resources";
import { RepositoryTypes } from "Build/SourceProviders/Common";
import { TaskAgentQueue } from "DistributedTask/Client/Contracts/TaskAgent";
import { Variable } from "Pipelines/Client/Contracts/Pipelines";
import { DemandCondition, IDemandData, IPipelineStageOption, ITemplateParameter, IVariableData } from "Types";
import { IObservableArray, IObservableObject, ObservableArray, ObservableObject, ObservableValue } from "VSS/Core/Observable";
import { format, localeIgnoreCaseComparer } from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { IServerException, IVssContributionService } from "VSS/Platform/Contribution";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";

const runPipelineParametersContributionId = "ms.vss-build-web.pipeline-run-parameters-data-provider";
const pipelineRunsContributionId = "ms.vss-build-web.pipeline-resource-runs-data-provider";
const buildRunsContributionId = "ms.vss-build-web.build-resource-runs-data-provider";
const containerRunsContributionId = "ms.vss-build-web.container-resource-runs-data-provider";
const orgSettingsContributionId = "ms.vss-build-web.pipelines-org-settings-queue-data-provider";
const projSettingsContributionId = "ms.vss-build-web.pipelines-general-settings-queue-data-provider";

const formatEqualsString = "{0} -equals {1}";
const equalsString = "equals";

const systemDebugVariableKey = "system.debug";
const agentDiagnosticVariableKey = "agent.diagnostic";
const systemVariablePrefix = "system.";

export interface IQueueBuildStoreOptions {
    pageContext: IVssPageContext;
    projectId: string;
    definitionId: number;
    buildDefinition?: BuildDefinition;
    buildRepository: BuildRepository;
    sourceBranch?: string;
    sourceVersionActionHub: SourceVersionActionHub;
    shelvesetPickerActiobHub: ShelvesetPickerActionHub;
    enableDiagnosticActionHub: EnableDiagnosticActionHub;
    queueActionHub: QueueActionHub;
    runtimeVariables: any[];
    previousTemplateParameters: { [key: string]: string };
    definitionVariables?: { [key: string]: BuildDefinitionVariable };
}

export interface IQueueBuildStore {
    demands: IObservableArray<any>;
    runtimeVariables: IObservableArray<IVariableData>;
}

export class QueueBuildStore extends Store implements IQueueBuildStore {
    private static _demandRegex: RegExp = /^([^\s]+)(\s+\-([^\s]+)\s+(.*))?/;
    public static key = "build-commands-queue-build-store";

    private _buildDefinition?: BuildDefinition;
    private _sourceBranch: string;
    private _sourceTag: string;
    private _sourceVersion: string = "";
    private _shelvesetName: string = "";
    private _runtimeVariables: IObservableArray<IVariableData>;
    private _definitionVariables?: { [key: string]: BuildDefinitionVariable };
    private _demands: IObservableArray<any>;
    private _pipelineRunParameters?: PipelineRunParametersData;
    private _buildResources?: IObservableArray<BuildResourceData>;
    private _containerResources?: IObservableArray<ContainerResourceData>;
    private _packageResources?: IObservableArray<PackageResourceData>;
    private _pipelineResources?: IObservableArray<PipelineResourceData>;
    private _repositoryResources?: IObservableArray<RepositoryResourceData>;
    private _templateParameters: IObservableArray<ITemplateParameter>;
    private _stageOptions: IObservableArray<IPipelineStageOption>;
    private _buildResourceRuns?: IObservableObject<BuildResourceRunData[]>;
    private _containerResourceRuns?: IObservableObject<ContainerResourceRunData[]>;
    private _pipelineResourceRuns?: IObservableObject<PipelineResourceRunData[]>;
    private _selectedQueue: TaskAgentQueue;
    private _pageContext: IVssPageContext;
    private _definitionId: number;
    private _projectId: string;
    private _buildRepository: BuildRepository;
    private _sourceVersionActionHub: SourceVersionActionHub;
    private _shelvesetPickerActiobHub: ShelvesetPickerActionHub;
    private _enableDiagnosticActionHub: EnableDiagnosticActionHub;
    private _buildClient: IBuildRestClient;
    private _queueActionHub: QueueActionHub;
    private _enableDiagnostic: boolean;
    private _runParametersAreLoaded: boolean = false;
    private _templateParametersAreValid = new ObservableValue<boolean>(true);
    private _previousTemplateParameters: { [key: string]: any } = {};
    private _errorMessage = new ObservableValue<string>("");
    private _orgSettings?: OrgSettingsData;
    private _projSettings?: GeneralSettingsData;

    constructor(options: IQueueBuildStoreOptions) {
        super();

        this._pageContext = options.pageContext;
        this._definitionId = options.definitionId;
        if (options.buildDefinition) {
            this._buildDefinition = options.buildDefinition;
        }

        this._sourceBranch = options.sourceBranch || "";
        this._projectId = options.projectId;
        this._sourceVersionActionHub = options.sourceVersionActionHub;
        this._shelvesetPickerActiobHub = options.shelvesetPickerActiobHub;
        this._buildRepository = options.buildRepository;
        this._queueActionHub = options.queueActionHub;
        this._definitionVariables = options.definitionVariables;
        this._runtimeVariables = new ObservableArray<IVariableData>(this._createRuntimeVariablesArray(options.runtimeVariables));
        this._enableDiagnosticActionHub = options.enableDiagnosticActionHub;

        this._sourceVersionActionHub.versionUpdated.addListener(this._handleVersionUpdated);
        this._sourceVersionActionHub.branchUpdated.addListener(this._handleBranchUpdated);
        this._sourceVersionActionHub.tagUpdated.addListener(this._handleTagUpdated);
        this._shelvesetPickerActiobHub.pickerDismissed.addListener(this._handleShelvesetUpdated);

        this._enableDiagnosticActionHub.toggleEnableDiagnostic.addListener(this._handleToggleEnableDiagnostic);

        this._queueActionHub.queueSelected.addListener(this._handleQueueSelected);

        this._demands = new ObservableArray<any>([]);
        this._stageOptions = new ObservableArray<IPipelineStageOption>([]);
        this._buildClient = this._pageContext.getRestClient<IBuildRestClient>("IBuildRestClient");

        this._templateParameters = new ObservableArray<ITemplateParameter>([]);
        this._previousTemplateParameters = options.previousTemplateParameters;

        this._repositoryResources = new ObservableArray<RepositoryResourceData>([]);

        if (isFeatureFlagEnabled(this._pageContext, "Build2.SetPipelineResources", false)) {
            this._pipelineResources = new ObservableArray<PipelineResourceData>([]);
            this._pipelineResourceRuns = new ObservableObject<PipelineResourceRunData[]>();
        }
        if (isFeatureFlagEnabled(this._pageContext, "Build2.SetBuildResources", false)) {
            this._buildResources = new ObservableArray<BuildResourceData>([]);
            this._buildResourceRuns = new ObservableObject<BuildResourceRunData[]>();
        }
        if (isFeatureFlagEnabled(this._pageContext, "Build2.SetContainerResources", false)) {
            this._containerResources = new ObservableArray<ContainerResourceData>([]);
            this._containerResourceRuns = new ObservableObject<ContainerResourceRunData[]>();
        }
        if (isFeatureFlagEnabled(this._pageContext, "Build2.SetPackageResources", false)) {
            this._packageResources = new ObservableArray<PackageResourceData>([]);
        }
    }

    public updateDefinition = (
        id: number,
        variables: any[],
        sourceBranch?: string,
        isDirtyDefinition?: boolean,
        buildRepository?: BuildRepository,
        definitionVariables?: { [key: string]: BuildDefinitionVariable },
        previousTemplateParameters?: { [key: string]: any }
    ) => {
        // If it is an unsaved definition, then don't reset it because we can't query the server for it
        if (!isDirtyDefinition) {
            this._buildDefinition = undefined;
        }
        this._definitionId = id;

        // reset enable diagnostics before parsing variables so we can use variables to determine if
        // enable diagnostics should be persisted from previous run
        this._enableDiagnostic = false;
        this._definitionVariables = definitionVariables;
        this._runtimeVariables = new ObservableArray<any>(this._createRuntimeVariablesArray(variables));

        // reset all the props since we are updating the pipeline
        this._previousTemplateParameters = previousTemplateParameters || {};
        this._sourceBranch = sourceBranch || "";
        this._sourceVersion = "";
        this._shelvesetName = "";
        this._sourceTag = "";
        this._selectedQueue = {} as TaskAgentQueue;
        this._buildRepository = buildRepository || ({} as BuildRepository);
        this._resetPipelineResources();
    };

    public updateBuildDefinition = (
        buildDefinition: BuildDefinition,
        variables: any[],
        definitionVariables?: { [key: string]: BuildDefinitionVariable },
        previousTemplateParameters?: { [key: string]: any }
    ) => {
        const sourceBranch = buildDefinition && buildDefinition.repository ? buildDefinition.repository.defaultBranch : "";
        this._buildDefinition = buildDefinition;
        if (buildDefinition && buildDefinition.id) {
            this.updateDefinition(
                buildDefinition.id,
                variables,
                sourceBranch,
                true,
                buildDefinition.repository,
                definitionVariables,
                previousTemplateParameters
            );
        }
    };

    public fetchBuildDefinition = (): Promise<BuildDefinition> => {
        if (this._buildDefinition && this._buildDefinition.id === this._definitionId) {
            return Promise.resolve(this._buildDefinition);
        }

        return this._buildClient.getDefinition(this._projectId, this._definitionId).then((retrievedDefinition: BuildDefinition) => {
            this._buildDefinition = retrievedDefinition;
            return this._buildDefinition;
        });
    };

    public fetchUpdatedDefaultBranch = (): Promise<string> => {
        return this._buildClient.getDefinition(this._projectId, this._definitionId).then((retrievedDefinition: BuildDefinition) => {
            return retrievedDefinition.repository.defaultBranch;
        });
    };

    public fetchDemands = () => {
        if (this._demands) {
            this._demands.removeAll();
        } else {
            this._demands = new ObservableArray<any>([]);
        }

        this.fetchBuildDefinition().then(
            (retrievedDefinition: BuildDefinition) => {
                const definitionDemands: IDemandData[] = this._convertSerializedDemandToDemandData(retrievedDefinition.demands);
                this._demands.value = definitionDemands;
            },
            error => {
                console.log("failed to retrieve build definition: " + error);
            }
        );
    };

    public async fetchPipelineResources(): Promise<void> {
        await this._fetchPipelineRunParameters();
    }

    public async fetchPipelineStages(): Promise<IPipelineStageOption[]> {
        this._stageOptions.removeAll();
        await this._fetchPipelineRunParameters();
        return this._stageOptions.value;
    }

    public async fetchOrgSettings(): Promise<OrgSettingsData | undefined> {
        await this._fetchOrgSettings();
        return this._orgSettings;
    }

    public async fetchProjSettings(): Promise<GeneralSettingsData | undefined> {
        await this._fetchProjSettings();
        return this._projSettings;
    }

    public async fetchPipelineResourceRuns(
        project: string,
        alias: string,
        name: string,
        keywordFilter: string,
        branch: string,
        tags: string[]
    ): Promise<void> {
        await this._fetchPipelineResourceRuns(project, alias, name, keywordFilter, branch, tags);
    }

    public async fetchBuildResourceRuns(alias: string, connectionId: string, buildType: string, name: string, branch: string): Promise<void> {
        await this._fetchBuildResourceRuns(alias, connectionId, buildType, name, branch);
    }

    public async fetchContainerResourceRuns(
        alias: string,
        connectionId: string,
        containerType: string,
        properties: { [key: string]: string }
    ): Promise<void> {
        await this._fetchContainerResourceRuns(alias, connectionId, containerType, properties);
    }

    public getBuildResourceRuns = (alias: string): BuildResourceRunData[] | undefined => {
        return this._buildResourceRuns && this._buildResourceRuns.get(alias);
    };

    public getContainerResourceRuns = (alias: string): ContainerResourceRunData[] | undefined => {
        return this._containerResourceRuns && this._containerResourceRuns.get(alias);
    };

    public getPipelineResourceRuns = (alias: string): PipelineResourceRunData[] | undefined => {
        return this._pipelineResourceRuns && this._pipelineResourceRuns.get(alias);
    };

    public async fetchPipelineTemplateParameters(): Promise<void> {
        this._templateParametersAreValid.value = false;
        await this._fetchPipelineRunParameters(true);
    }

    public getRunTimeVariables(): IDictionaryStringTo<Variable> {
        const variables: IDictionaryStringTo<Variable> = {};
        this._runtimeVariables.value.forEach(variable => {
            if (variable.name && variable.name.trim() && (!variable.isSecret || variable.value)) {
                variables[variable.name] = {
                    value: variable.value,
                    isSecret: variable.isSecret
                };
            }
        });

        // add the diagnostic variables
        if (this._enableDiagnostic) {
            variables[systemDebugVariableKey] = {
                value: "true",
                isSecret: false
            };
            variables[agentDiagnosticVariableKey] = {
                value: "true",
                isSecret: false
            };
        }

        return variables;
    }

    public getSerializedVariables(): string {
        const variables: IDictionaryStringTo<string> = {};

        this._runtimeVariables.value.forEach((variable: any) => {
            // for secret variables only add them to payload when value is set at queue time
            if (variable.name && variable.name.trim() && (!variable.isSecret || variable.value)) {
                variables[variable.name] = variable.value;
            }
        });

        // add the diagnostic variables
        if (this._enableDiagnostic) {
            variables[systemDebugVariableKey] = "true";
            variables[agentDiagnosticVariableKey] = "true";
        }

        return JSON.stringify(variables);
    }

    public getVariables = () => {
        return this._runtimeVariables;
    };

    public getDemandsValues() {
        return this._convertDemandDataToSerializedDemand();
    }

    public get demands() {
        return this._demands;
    }

    public get runtimeVariables() {
        return this._runtimeVariables;
    }

    public get buildResources() {
        return this._buildResources;
    }

    public get containerResources() {
        return this._containerResources;
    }

    public get packageResources() {
        return this._packageResources;
    }

    public get pipelineResources() {
        return this._pipelineResources;
    }

    public get repositoryResources() {
        return this._repositoryResources;
    }

    public get buildResourceRuns() {
        return this._buildResourceRuns;
    }

    public get containerResourceRuns() {
        return this._containerResourceRuns;
    }

    public get pipelineResourceRuns() {
        return this._pipelineResourceRuns;
    }

    public get templateParameters() {
        return this._templateParameters;
    }

    public get templateParametersAreValid() {
        return this._templateParametersAreValid;
    }

    public get runParametersAreLoaded(): boolean {
        return !!this._pipelineRunParameters && this._runParametersAreLoaded;
    }

    public get templateParametersAreLoaded(): boolean {
        return !!this._pipelineRunParameters && !!this._pipelineRunParameters.templateParameters;
    }

    public get stageOptions() {
        return this._stageOptions;
    }

    public get skippedStageNames() {
        return this._stageOptions.value.filter(s => s.skip).map(s => s.refName);
    }

    public get errorMessage() {
        return this._errorMessage;
    }

    public getSourceBranch() {
        if (this._buildRepository && this._buildRepository.type && this._isGit(this._buildRepository.type)) {
            // checking for "refs/" prefix here means users can queue from refs/pull/* PR Git refs
            if (
                this._sourceBranch &&
                !this._sourceBranch.startsWith(GitConstants.RefsHeadsPrefix) &&
                !this._sourceBranch.startsWith(GitConstants.RefsPrefix)
            ) {
                return GitConstants.RefsHeadsPrefix + this._sourceBranch;
            } else if (this._sourceTag) {
                return this._sourceTag.startsWith(GitConstants.RefsTagsPrefix) ? this._sourceTag : GitConstants.RefsTagsPrefix + this._sourceTag;
            }
        }

        return this._sourceBranch;
    }

    public getSourceVersion() {
        return this._sourceVersion;
    }

    public getShelvesetName() {
        return this._shelvesetName;
    }

    public getSelectedQueue() {
        return this._selectedQueue;
    }

    public getEnableDiagnosticState() {
        return this._enableDiagnostic;
    }

    public getShelvesetPickerActionHub(): ShelvesetPickerActionHub {
        return this._shelvesetPickerActiobHub;
    }

    public getSourceControlSelectorActionHub(): SourceVersionActionHub {
        return this._sourceVersionActionHub;
    }

    public getEnableDiagnosticActionHub(): EnableDiagnosticActionHub {
        return this._enableDiagnosticActionHub;
    }

    public dispose() {
        this._sourceVersionActionHub.versionUpdated.removeListener(this._handleVersionUpdated);
        this._sourceVersionActionHub.branchUpdated.removeListener(this._handleBranchUpdated);
        this._sourceVersionActionHub.tagUpdated.removeListener(this._handleTagUpdated);
        this._shelvesetPickerActiobHub.pickerDismissed.removeListener(this._handleBranchUpdated);
        this._queueActionHub.queueSelected.removeListener(this._handleQueueSelected);
    }

    /**
     * Update the selected branch without overwriting the commit.
     *
     * Separate from _handleBranchUpdated method because the Action callback pattern can only take one parameter
     */
    public updateBranchForCommit = (newBranch: string) => {
        this._sourceTag = "";
        this._sourceBranch = newBranch;
        this.emitChanged();
    };

    public updateVariable = (variable: IVariableData, index: number) => {
        this._runtimeVariables.change(index, variable);
    };

    public addVariable = (variable: IVariableData) => {
        this._runtimeVariables.push(variable);
    };

    public deleteVariable = (index: number) => {
        this._runtimeVariables.splice(index, 1);
    };

    public addDemand = (demand: IDemandData) => {
        this._demands.push(demand);
    };

    public deleteDemand = (index: number) => {
        this._demands.splice(index, 1);
    };

    public updateDemand = (demand: IDemandData) => {
        this._demands.change(demand.index, demand);
    };

    public updateStageOptions = (updatedStages: IPipelineStageOption[]) => {
        this._stageOptions.value = updatedStages.map(stageOption => ({ ...stageOption }));
    };

    public updateRepositoryResource = (repository: RepositoryResourceData) => {
        if (this._repositoryResources && repository) {
            const index = this._repositoryResources.value.findIndex(r => r.alias === repository.alias);
            if (index > -1) {
                this._repositoryResources.change(index, { ...repository });
            }
        }
    };

    /** Returns an array of repository resources that have updated refs or versions from those specified in YAML */
    public getModifiedRepositoryResources(): RepositoryResourceData[] {
        const originals = (this._pipelineRunParameters && this._pipelineRunParameters.repositoryResources) || [];
        const resources = (this._repositoryResources && this._repositoryResources.value) || [];
        return resources.filter(resource => {
            const original = originals.find(value => {
                return value.alias === resource.alias;
            });
            return !(original && resource.refName === original.refName && resource.version === original.version);
        });
    }

    public updatePipelineResource = (pipeline: PipelineResourceData) => {
        if (this._pipelineResources && pipeline) {
            const index = this._pipelineResources.value.findIndex(p => p.alias === pipeline.alias);
            if (index > -1) {
                this._pipelineResources.change(index, { ...pipeline });
            }
        }
    };

    public updateBuildResource = (build: BuildResourceData) => {
        if (this._buildResources && build) {
            const index = this._buildResources.value.findIndex(b => b.alias === build.alias);
            if (index > -1) {
                this._buildResources.change(index, { ...build });
            }
        }
    };

    public updateContainerResource = (container: ContainerResourceData) => {
        if (this._containerResources && container) {
            const index = this._containerResources.value.findIndex(b => b.alias === container.alias);
            if (index > -1) {
                this._containerResources.change(index, { ...container });
            }
        }
    };

    public getModifiedBuildResources(): BuildResourceData[] {
        const originals = (this._pipelineRunParameters && this._pipelineRunParameters.buildResources) || [];
        const resources = (this._buildResources && this._buildResources.value) || [];
        return resources.filter(resource => {
            const original = originals.find(value => {
                return value.alias === resource.alias;
            });
            return !(original && resource.version === original.version);
        });
    }

    public getModifiedContainerResources(): ContainerResourceData[] {
        const originals = (this._pipelineRunParameters && this._pipelineRunParameters.containerResources) || [];
        const resources = (this._containerResources && this._containerResources.value) || [];
        return resources.filter(resource => {
            const original = originals.find(value => {
                return value.alias === resource.alias;
            });
            return !(original && resource.version === original.version);
        });
    }

    public updatePackageResource = (packageValue: PackageResourceData) => {
        if (this._packageResources && packageValue) {
            const index = this._packageResources.value.findIndex(p => p.alias === packageValue.alias);
            if (index > -1) {
                this._packageResources.change(index, { ...packageValue });
            }
        }
    };

    /** Returns an array of package resources that have updated versions from those specified in YAML */
    public getModifiedPackageResources(): PackageResourceData[] {
        const originals = (this._pipelineRunParameters && this._pipelineRunParameters.packageResources) || [];
        const resources = (this._packageResources && this._packageResources.value) || [];
        return resources.filter(resource => {
            const original = originals.find(value => {
                return value.alias === resource.alias;
            });
            return !(original && resource.version === original.version);
        });
    }

    /** Returns an array of pipeline resources that have updated versions from those specified in YAML */
    public getModifiedPipelineResources(): PipelineResourceData[] {
        const originals = (this._pipelineRunParameters && this._pipelineRunParameters.pipelineResources) || [];
        const resources = (this._pipelineResources && this._pipelineResources.value) || [];
        return resources.filter(resource => {
            const original = originals.find(value => {
                return value.alias === resource.alias;
            });
            return !(original && resource.version === original.version);
        });
    }

    public updateTemplateParameter = (templateParameter: ITemplateParameter) => {
        const index = this._templateParameters.value.findIndex(p => p.data.name === templateParameter.data.name);
        if (index > -1) {
            const updatedParameter = {
                data: templateParameter.data,
                value: templateParameter.value,
                errorMessage: this._validateTemplateParameterInput(templateParameter.data, templateParameter.value)
            };

            this._templateParameters.change(index, updatedParameter);

            // check for error state change
            if (templateParameter.errorMessage !== updatedParameter.errorMessage) {
                this._updateTemplateParametersValidity();
            }

            this._resetParameterDependentResources();
        }
    };

    public getTemplateParametersDictionary(): { [key: string]: string } | undefined {
        const templateParameters = this.templateParameters && this.templateParameters.value;
        let templateParameterMap: { [key: string]: string } | undefined = undefined;
        if (templateParameters && templateParameters.length > 0) {
            templateParameterMap = {};
            templateParameters.forEach(parameter => {
                templateParameterMap![parameter.data.name] = parameter.value || parameter.data.default;
            });
        }
        return templateParameterMap;
    }

    private _createRuntimeVariablesArray = (variables: any[]): IVariableData[] => {
        const result: IVariableData[] = [];
        if (variables && variables.length > 0) {
            variables.forEach((variable: any, index: number) => {
                // Don`t add system variables if not manually set and overriden by user and listed in pipeline definition
                // If they are in pipeline definition instead of removal we will set the value to be whatever the corresponding definition variable value is
                if (variable.name.startsWith(systemVariablePrefix) || variable.name.startsWith(agentDiagnosticVariableKey)) {
                    if (variable.variable.disableDelete) {
                        const debugDefinitionVariable = this._definitionVariables && this._definitionVariables[variable.name];
                        if (debugDefinitionVariable) {
                            variable.variable.value = debugDefinitionVariable.value;
                        }
                    } else {
                        return;
                    }
                }

                result.push({
                    index: index,
                    name: variable.name,
                    value: variable.variable.value,
                    isSecret: variable.variable.isSecret,
                    disableDelete: variable.variable.disableDelete
                } as IVariableData);
            });
        }

        // If system.debug and agent.diagnostic are both present and "true", remove them and set
        // enableDiagnostic to true to persist the user's "enable system diagnostics" selection from the previous run.
        // The caveat is that if they are definition variables we won't remove them from the variables list.
        // Instead we will set the value to be whatever the corresponding definition variable value is
        const debugVariableIndex = result.findIndex(v => v.name === systemDebugVariableKey && v.value === "true");
        const diagVariableIndex = result.findIndex(v => v.name === agentDiagnosticVariableKey && v.value === "true");
        if (debugVariableIndex > -1 && diagVariableIndex > -1) {
            const debugVariable = result[debugVariableIndex];
            const diagVariable = result[diagVariableIndex];

            if (debugVariable.disableDelete) {
                const debugDefinitionVariable = this._definitionVariables && this._definitionVariables[systemDebugVariableKey];
                if (debugDefinitionVariable) {
                    debugVariable.value = debugDefinitionVariable.value;
                }
            } else {
                result.splice(debugVariableIndex, 1);
            }

            if (diagVariable.disableDelete) {
                const diagDefinitionVariable = this._definitionVariables && this._definitionVariables[agentDiagnosticVariableKey];
                if (diagDefinitionVariable) {
                    diagVariable.value = diagDefinitionVariable.value;
                }
            } else {
                const updatedDiagVariableIndex = result.findIndex(v => v.name === agentDiagnosticVariableKey);
                result.splice(updatedDiagVariableIndex, 1);
            }

            this._handleToggleEnableDiagnostic(true);
        }

        return result;
    };

    private _isGit = (type: string): boolean => {
        const lowerCaseType = type.toLowerCase();
        return (
            lowerCaseType === RepositoryTypes.Bitbucket.toLowerCase() ||
            lowerCaseType === RepositoryTypes.ExternalGit.toLowerCase() ||
            lowerCaseType === RepositoryTypes.GitHub.toLowerCase() ||
            lowerCaseType === RepositoryTypes.GitHubEnterprise.toLowerCase() ||
            lowerCaseType === RepositoryTypes.TfsGit.toLowerCase()
        );
    };

    private _handleQueueSelected = (queue: TaskAgentQueue) => {
        this._selectedQueue = queue;
        // Refresh the demands when the queue selection has changed
        this.fetchDemands();
        this.emitChanged();
    };

    private _handleBranchUpdated = (newBranch: string) => {
        this._resetPipelineSources();
        this._sourceBranch = newBranch;
        this._resetPipelineResources();
        this.emitChanged();
    };

    private _handleTagUpdated = (newTag: string) => {
        this._resetPipelineSources();
        this._sourceTag = newTag;
        this._resetPipelineResources();
        this.emitChanged();
    };

    private _handleShelvesetUpdated = (shelvesetName: string) => {
        this._shelvesetName = shelvesetName;
        this.emitChanged();
    };

    private _handleToggleEnableDiagnostic = (enabled: boolean) => {
        this._enableDiagnostic = enabled;
        this.emitChanged();
    };

    private _handleVersionUpdated = (versionString: string) => {
        this._sourceVersion = versionString;
        this._resetPipelineResources();
        this.emitChanged();
    };

    private _resetPipelineSources = (): void => {
        this._sourceBranch = "";
        this._sourceTag = "";
        this._sourceVersion = "";
    };

    private _resetPipelineResources() {
        this._errorMessage.value = "";
        this._runParametersAreLoaded = false;
        this._pipelineRunParameters = undefined;
        this._templateParameters.value = [];

        // explicity set new list so subscribers always get notified
        if (this._repositoryResources) {
            this._repositoryResources.value = [];
        }
        if (this._pipelineResources) {
            this._pipelineResources.value = [];
        }
        if (this._buildResources) {
            this._buildResources.value = [];
        }
        if (this._containerResources) {
            this._containerResources.value = [];
        }
        if (this._pipelineResourceRuns && this._pipelineResourceRuns.keys().length) {
            this._pipelineResourceRuns = new ObservableObject<PipelineResourceRunData[]>();
        }
        if (this._buildResourceRuns && this._buildResourceRuns.keys().length) {
            this._buildResourceRuns = new ObservableObject<BuildResourceRunData[]>();
        }
        if (this._containerResourceRuns && this._containerResourceRuns.keys().length) {
            this._containerResourceRuns = new ObservableObject<ContainerResourceRunData[]>();
        }

        if (this._packageResources) {
            this._packageResources.value = [];
        }
        this._stageOptions.value = [];
    }

    /**
     * Any advanced option that needs to be re-fetched when template parameters change
     */
    private _resetParameterDependentResources() {
        if (this._repositoryResources) {
            this._repositoryResources.value = [];
        }
        if (this._pipelineResourceRuns && this._pipelineResourceRuns.keys().length) {
            this._pipelineResourceRuns = new ObservableObject<PipelineResourceRunData[]>();
        }
        if (this._buildResourceRuns && this._buildResourceRuns.keys().length) {
            this._buildResourceRuns = new ObservableObject<BuildResourceRunData[]>();
        }
        if (this._containerResourceRuns && this._containerResourceRuns.keys().length) {
            this._containerResourceRuns = new ObservableObject<ContainerResourceRunData[]>();
        }
        this._stageOptions.value = [];
        this._runParametersAreLoaded = false;
    }

    private _convertDemandDataToSerializedDemand(): string[] {
        const demandSerialized: string[] = [];
        const demands = this._demands.value || [];

        demands.forEach((demand: IDemandData) => {
            switch (demand.condition) {
                case DemandCondition.Exists:
                    demandSerialized.push(demand.name.trim());
                    break;
                case DemandCondition.Equals:
                    demandSerialized.push(format(formatEqualsString, demand.name.trim(), demand.value.trim()));
                    break;
                default:
                    break;
            }
        });
        return demandSerialized;
    }

    private _convertSerializedDemandToDemandData(serializedDemands: any[]): IDemandData[] {
        const demands: IDemandData[] = [];
        if (serializedDemands && serializedDemands.length > 0) {
            serializedDemands.forEach((serializedDemand: any, index: number) => {
                const demand: IDemandData = { index: index, name: "", condition: DemandCondition.Exists, value: "" };
                const match: RegExpExecArray = QueueBuildStore._demandRegex.exec(serializedDemand) || ({} as RegExpExecArray);

                if (match) {
                    demand.name = !match[1] ? "" : match[1];
                    demand.value = !match[4] ? "" : match[4];

                    if (match[3] && match[3].length > 0 && equalsString === match[3].toLocaleLowerCase()) {
                        demand.condition = DemandCondition.Equals;
                    } else {
                        demand.condition = DemandCondition.Exists;
                    }
                }

                demands.push(demand);
            });
        }

        return demands;
    }

    private _fetchPipelineRunParameters(onlyFetchTemplateParameters = false): Promise<void> {
        return new Promise(resolve => {
            if (this.runParametersAreLoaded && !onlyFetchTemplateParameters) {
                this._updateFromRunParameters();
                resolve();
            } else {
                const params: PipelineRunParametersRequestParams = {
                    pipelineId: this._definitionId,
                    sourceBranch: this.getSourceBranch(),
                    sourceVersion: this.getSourceVersion(),
                    onlyFetchTemplateParameters,
                    templateParameters: this.getTemplateParametersDictionary() || {}
                };
                const contributionService = this._pageContext.getService<IVssContributionService>("IVssContributionService");
                contributionService
                    .getDataAsync<PipelineRunParametersData>(runPipelineParametersContributionId, params, true)
                    .then((runParams: PipelineRunParametersData) => {
                        const ex: IServerException = contributionService.getDataProviderExceptions()[runPipelineParametersContributionId];
                        if (runParams) {
                            this._pipelineRunParameters = runParams;
                            if (onlyFetchTemplateParameters) {
                                this._updateTemplateParameters();
                            } else {
                                this._updateFromRunParameters();
                                this._runParametersAreLoaded = true;
                            }
                            this._setErrorMessage(onlyFetchTemplateParameters);
                            resolve();
                        } else if (ex && ex.exceptionType === "YamlFileNotFoundException") {
                            this._errorMessage.value = Resources.YamlMissingError;
                        } else {
                            const message = ex && ex.message;
                            console.log("Failed to retrieve pipeline resources and stages: " + message);
                            resolve();
                        }
                    });
            }
        });
    }

    private _fetchOrgSettings(): Promise<void> {
        return new Promise(resolve => {
            const contributionService = this._pageContext.getService<IVssContributionService>("IVssContributionService");
            contributionService.getDataAsync<OrgSettingsData>(orgSettingsContributionId, undefined).then((orgSettings: OrgSettingsData) => {
                this._orgSettings = orgSettings;
                resolve();
            });
        });
    }

    private _fetchProjSettings(): Promise<void> {
        return new Promise(resolve => {
            const contributionService = this._pageContext.getService<IVssContributionService>("IVssContributionService");
            contributionService.getDataAsync<GeneralSettingsData>(projSettingsContributionId, undefined).then((projSettings: GeneralSettingsData) => {
                this._projSettings = projSettings;
                resolve();
            });
        });
    }

    private _updateFromRunParameters() {
        const params = this._pipelineRunParameters;
        if (!params) {
            return;
        }

        if (params.stages && this._stageOptions) {
            const refNameToDisplayName: { [refName: string]: string } = {};
            params.stages.forEach(s => (refNameToDisplayName[s.refName.toLowerCase()] = s.name));

            this._stageOptions.value = params.stages.map(stage => {
                return {
                    name: stage.name,
                    refName: stage.refName,
                    dependsOn: (stage.dependsOn || []).map(d => refNameToDisplayName[d.toLowerCase()]),
                    skip: false
                };
            });
        }

        if (params.buildResources && this._buildResources) {
            this._buildResources.value = params.buildResources
                .map(build => {
                    return { ...build };
                })
                .sort((a, b) => localeIgnoreCaseComparer(a.name, b.name));
        }

        if (params.containerResources && this._containerResources) {
            this._containerResources.value = params.containerResources
                .map(build => {
                    return { ...build };
                })
                .sort((a, b) => localeIgnoreCaseComparer(a.name, b.name));
        }

        if (params.packageResources && this._packageResources) {
            this._packageResources.value = params.packageResources
                .map(packageValue => {
                    return { ...packageValue };
                })
                .sort((a, b) => localeIgnoreCaseComparer(a.name, b.name));
        }

        if (params.pipelineResources && this._pipelineResources) {
            this._pipelineResources.value = params.pipelineResources
                .map(pipeline => {
                    return { ...pipeline };
                })
                .sort((a, b) => localeIgnoreCaseComparer(a.name, b.name));
        }

        if (params.repositoryResources && this._repositoryResources) {
            this._repositoryResources.value = params.repositoryResources
                .map(repository => {
                    return { ...repository };
                })
                .sort((a, b) => localeIgnoreCaseComparer(a.name, b.name));
        }
    }

    private _updateTemplateParameters() {
        const templateParameters = this._pipelineRunParameters && this._pipelineRunParameters.templateParameters;
        if (!templateParameters) {
            return;
        }

        this._templateParameters.value = templateParameters.map(templateParam => {
            const defaultValue =
                this._previousTemplateParameters[templateParam.name] ||
                templateParam.default ||
                (templateParam.values && templateParam.values.length > 0 && templateParam.values[0]) ||
                (templateParam.type === TemplateParameterInputType.Checkbox && "false") ||
                "";

            return {
                data: { ...templateParam },
                value: defaultValue,
                errorMessage: this._validateTemplateParameterInput(templateParam, defaultValue)
            };
        });

        this._updateTemplateParametersValidity();
    }

    private _setErrorMessage(updateTemplateParametersValidity: boolean) {
        const errors = this._pipelineRunParameters && this._pipelineRunParameters.errors;
        if (!errors || errors.length === 0) {
            return;
        }

        // create newline-separated list of errors from parsing YAML
        this._errorMessage.value = format(Resources.YamlParseError, errors.join("\n"));
        if (updateTemplateParametersValidity) {
            this._templateParametersAreValid.value = false;
        }
    }

    private _validateTemplateParameterInput(parameter: TemplateParameterData, value: string): string | undefined {
        // dropdowns, checkboxes, and radio buttons can't have errors
        if (
            !parameter ||
            parameter.type === TemplateParameterInputType.Checkbox ||
            parameter.type === TemplateParameterInputType.Dropdown ||
            parameter.type === TemplateParameterInputType.RadioButtonGroup
        ) {
            return undefined;
        }

        const trimmedValue = value.trim();

        // check for missing input - don't need to display an error message because field is already labelled as required
        if (!trimmedValue && !parameter.default) {
            return "";
        }

        // check that numeric parameters have numeric input
        if (parameter.type === TemplateParameterInputType.NumericTextField && isNaN(Number(trimmedValue))) {
            return Resources.NumericInputErrorMessage;
        }

        // there was no error to show the user
        return undefined;
    }

    private _updateTemplateParametersValidity() {
        const templateParameters = this._templateParameters.value;
        if (templateParameters) {
            let isValid = true;
            for (let i = 0; i < templateParameters.length; i++) {
                const param = templateParameters[i];
                if (param.errorMessage !== undefined) {
                    isValid = false;
                    break;
                }
            }

            // only update if validity changed
            if (this._templateParametersAreValid.value !== isValid) {
                this._templateParametersAreValid.value = isValid;
            }
        }
    }

    private _fetchPipelineResourceRuns(
        project: string,
        alias: string,
        name: string,
        keywordFilter: string,
        branch: string,
        tags: string[]
    ): Promise<void> {
        return new Promise((resolve: () => void) => {
            const params: PipelineResourceRunsRequestParams = {
                keywordFilter: keywordFilter,
                project: project,
                source: name,
                branch: branch,
                tags: tags
            };
            const contributionService = this._pageContext.getService<IVssContributionService>("IVssContributionService");
            contributionService.getDataAsync<PipelineResourceRunData[]>(pipelineRunsContributionId, params, true).then(runsData => {
                if (runsData) {
                    this._pipelineResourceRuns = new ObservableObject<PipelineResourceRunData[]>();
                    this._pipelineResourceRuns.add(alias, runsData);
                    resolve();
                } else {
                    const ex: IServerException = contributionService.getDataProviderExceptions()[pipelineRunsContributionId];
                    const message = ex && ex.message;
                    console.log("Failed to retrieve pipeline runs for the resource: " + message);
                    resolve();
                }
            });
        });
    }

    private _fetchBuildResourceRuns(alias: string, connectionId: string, buildType: string, name: string, branch: string): Promise<void> {
        return new Promise((resolve: () => void) => {
            const params: BuildResourceRunsRequestParams = {
                connectionId: connectionId,
                source: name,
                buildType: buildType,
                branch: branch
            };
            const contributionService = this._pageContext.getService<IVssContributionService>("IVssContributionService");
            contributionService.getDataAsync<BuildResourceRunData[]>(buildRunsContributionId, params, true).then(runsData => {
                if (runsData) {
                    if (!this._buildResourceRuns) {
                        this._buildResourceRuns = new ObservableObject<BuildResourceRunData[]>();
                    }

                    this._buildResourceRuns.add(alias, runsData);
                    resolve();
                } else {
                    const ex: IServerException = contributionService.getDataProviderExceptions()[buildRunsContributionId];
                    if (ex && ex.exceptionType === "AccessCheckException") {
                        this._errorMessage.value = ex.message;
                    } else {
                        const message = ex && ex.message;
                        console.log("Failed to retrieve build runs for the resource: " + message);
                    }
                    resolve();
                }
            });
        });
    }

    private _fetchContainerResourceRuns(
        alias: string,
        connectionId: string,
        containerType: string,
        properties: { [key: string]: string }
    ): Promise<void> {
        return new Promise((resolve: () => void) => {
            const params: ContainerResourceRunsRequestParams = {
                connectionId: connectionId,
                properties: properties,
                containerType: containerType
            };
            const contributionService = this._pageContext.getService<IVssContributionService>("IVssContributionService");
            contributionService.getDataAsync<ContainerResourceRunData[]>(containerRunsContributionId, params, true).then(runsData => {
                if (runsData) {
                    if (!this._containerResourceRuns) {
                        this._containerResourceRuns = new ObservableObject<ContainerResourceRunData[]>();
                    }

                    this._containerResourceRuns.add(alias, runsData);
                    resolve();
                } else {
                    const ex: IServerException = contributionService.getDataProviderExceptions()[buildRunsContributionId];
                    const message = ex && ex.message;
                    console.log("Failed to retrieve container images for the resource: " + message);
                    resolve();
                }
            });
        });
    }
}

interface IDictionaryStringTo<T> {
    [key: string]: T;
}
